export const RECORD_LOCK_CLIENT_PARAM = 'clientId';
export const RECORD_LOCK_CLIENT_HEADER = 'X-Client-Id';

/**
 * Per-tab editing-session identity. It is intentionally generated once per page
 * load: two tabs of the same account must be different lock holders.
 */
export const RECORD_LOCK_CLIENT_ID: string =
  globalThis.crypto?.randomUUID?.() ??
  `c-${Date.now().toString(36)}-${performance.now().toString(36)}`;

type RequestWithParams = {
  params?: unknown;
};

type RequestWithHeaders = {
  headers?: unknown;
};

type HeaderSetter = {
  set: (name: string, value: string) => void;
};

function hasHeaderSetter(headers: unknown): headers is HeaderSetter {
  return !!headers && typeof (headers as HeaderSetter).set === 'function';
}

/** Add the record-lock client id as normal REST request metadata. */
export function attachRecordLockClientHeader<T extends RequestWithHeaders>(config: T): T {
  if (hasHeaderSetter(config.headers)) {
    config.headers.set(RECORD_LOCK_CLIENT_HEADER, RECORD_LOCK_CLIENT_ID);
    return config;
  }

  if (config.headers && typeof config.headers === 'object' && !Array.isArray(config.headers)) {
    (config.headers as Record<string, unknown>)[RECORD_LOCK_CLIENT_HEADER] = RECORD_LOCK_CLIENT_ID;
    return config;
  }

  config.headers = { [RECORD_LOCK_CLIENT_HEADER]: RECORD_LOCK_CLIENT_ID };
  return config;
}

/**
 * Add the record-lock client id as a query param for explicit fallback calls
 * that must survive gateway/proxy paths even when custom headers are stripped.
 */
export function attachRecordLockClientParam<T extends RequestWithParams>(config: T): T {
  const existing = config.params;
  if (typeof URLSearchParams !== 'undefined' && existing instanceof URLSearchParams) {
    if (!existing.has(RECORD_LOCK_CLIENT_PARAM)) {
      existing.set(RECORD_LOCK_CLIENT_PARAM, RECORD_LOCK_CLIENT_ID);
    }
    return config;
  }

  if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
    const params = existing as Record<string, unknown>;
    if (params[RECORD_LOCK_CLIENT_PARAM] === undefined) {
      params[RECORD_LOCK_CLIENT_PARAM] = RECORD_LOCK_CLIENT_ID;
    }
    return config;
  }

  config.params = { [RECORD_LOCK_CLIENT_PARAM]: RECORD_LOCK_CLIENT_ID };
  return config;
}
